import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Drawer,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useAccounts } from './accountsApi';
import { useCreateManualEntry } from './journalApi';

// ERPNext's best journal-form idea, kept: a live الفرق (Dr − Cr)
// indicator that must reach 0.00 before the post button enables.

interface Props {
  opened: boolean;
  onClose: () => void;
}

interface DraftLine {
  key: number;
  accountId: string | null;
  debit: number;
  credit: number;
  memo: string;
}

const emptyLine = (key: number): DraftLine => ({
  key, accountId: null, debit: 0, credit: 0, memo: '',
});

export function ManualEntryDrawer({ opened, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const accounts = useAccounts(false);
  const create = useCreateManualEntry();
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const [entryDate, setEntryDate] = useState<Date | null>(new Date());
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(1), emptyLine(2)]);

  useEffect(() => {
    if (!opened) return;
    setEntryDate(new Date());
    setDescription('');
    setLines([emptyLine(1), emptyLine(2)]);
  }, [opened]);

  const accountOptions = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.isPostable && a.isActive)
        .map((a) => ({
          value: a.accountId,
          label: `${a.code} — ${i18n.language === 'ar' ? a.nameAr : (a.nameEn ?? a.nameAr)}`,
        })),
    [accounts.data, i18n.language],
  );

  const totalDebit = lines.reduce((s, l) => s + (l.debit || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (l.credit || 0), 0);
  const difference = round2(totalDebit - totalCredit);
  const valid =
    entryDate !== null &&
    difference === 0 &&
    totalDebit > 0 &&
    lines.every(
      (l) =>
        l.accountId !== null &&
        (l.debit > 0) !== (l.credit > 0),
    );

  const patchLine = (key: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const submit = async () => {
    try {
      await create.mutateAsync({
        entryDate: toIsoDate(entryDate!),
        description: description.trim() || null,
        lines: lines.map((l) => ({
          accountId: l.accountId!,
          debit: l.debit || 0,
          credit: l.credit || 0,
          memo: l.memo.trim() || null,
        })),
      });
      notifications.show({ color: 'green', title: t('common.toasts.created'), message: '' });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('accounting.journal.manual.title')}
      position={drawerPosition}
      size="xl"
      padding="lg"
    >
      <Stack gap="md">
        <Group grow>
          <DatePickerInput
            label={t('accounting.journal.manual.dateLabel')}
            value={entryDate}
            onChange={setEntryDate}
          />
          <TextInput
            label={t('accounting.journal.manual.descriptionLabel')}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
        </Group>

        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: '40%' }}>{t('accounting.journal.columns.account')}</Table.Th>
              <Table.Th>{t('accounting.journal.columns.debit')}</Table.Th>
              <Table.Th>{t('accounting.journal.columns.credit')}</Table.Th>
              <Table.Th>{t('accounting.journal.columns.memo')}</Table.Th>
              <Table.Th style={{ width: 44 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((line) => (
              <Table.Tr key={line.key}>
                <Table.Td>
                  <Select
                    data={accountOptions}
                    value={line.accountId}
                    onChange={(v) => patchLine(line.key, { accountId: v })}
                    searchable
                    placeholder={t('accounting.journal.manual.accountPlaceholder')}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={line.debit}
                    onChange={(v) => patchLine(line.key, { debit: Number(v) || 0, credit: 0 })}
                    min={0}
                    decimalScale={2}
                    hideControls
                    className="font-numeric"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={line.credit}
                    onChange={(v) => patchLine(line.key, { credit: Number(v) || 0, debit: 0 })}
                    min={0}
                    decimalScale={2}
                    hideControls
                    className="font-numeric"
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    value={line.memo}
                    onChange={(e) => patchLine(line.key, { memo: e.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  {lines.length > 2 && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => setLines((ls) => ls.filter((l) => l.key !== line.key))}
                    >
                      <IconTrash size={16} stroke={1.6} />
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Group justify="space-between">
          <Button
            variant="light"
            leftSection={<IconPlus size={16} stroke={1.6} />}
            onClick={() =>
              setLines((ls) => [...ls, emptyLine(Math.max(...ls.map((l) => l.key)) + 1)])
            }
          >
            {t('accounting.journal.manual.addLine')}
          </Button>
          <Group gap="lg">
            <Text size="sm" className="font-numeric">
              {t('accounting.journal.columns.debit')}: {totalDebit.toFixed(2)}
            </Text>
            <Text size="sm" className="font-numeric">
              {t('accounting.journal.columns.credit')}: {totalCredit.toFixed(2)}
            </Text>
            <Text
              size="sm"
              fw={700}
              c={difference === 0 ? 'green' : 'red'}
              className="font-numeric"
            >
              {t('accounting.journal.manual.difference')}: {difference.toFixed(2)}
            </Text>
          </Group>
        </Group>

        {difference !== 0 && totalDebit + totalCredit > 0 && (
          <Alert color="yellow">{t('accounting.journal.manual.unbalancedHint')}</Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!valid} loading={create.isPending}>
            {t('accounting.journal.manual.post')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
